import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import { channels, seoKeywords } from "@/mocks/siteData";
import ChannelDetail from "@/pages/channels/components/ChannelDetail";
import TipForm from "@/pages/channels/components/TipForm";

const pageTitle = "投稿通道 - 图文·匿名私信·视频素材·录音证据四类匿名投稿入口 | 潜声投稿";
const pageDesc =
  "潜声投稿提供四类独立投稿通道：图文投稿通道提交文字与截图，匿名私信投稿一对一沟通，视频素材上传现场与监控画面，录音证据提交通话与会议音频。全程可匿名，大文件走一次性安全链接。";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: pageDesc,
    inLanguage: "zh-CN",
    url: "https://qiansheng.online/channels",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://qiansheng.online/" },
      { "@type": "ListItem", position: 2, name: "投稿通道", item: "https://qiansheng.online/channels" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "潜声投稿的四类投稿通道",
    itemListElement: channels.map((channel, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: channel.name,
      description: channel.summary,
      url: `https://qiansheng.online/channels#${channel.slug}`,
    })),
  },
];

const noticeList = [
  "同一件事建议只选一个主通道，后续补充材料时沿用同一条线索，便于编辑归档。",
  "线索描述请聚焦事实，避免使用情绪化判断，也无需为了吸引注意而夸大情节。",
  "若涉及未成年人、正在进行的纠纷或人身安全风险，请在描述中单独说明，我们会优先处理。",
  "任何情况下都不要向个人账号转账或支付所谓「爆料费」，本平台不收取任何投稿费用。",
];

export default function Channels() {
  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: seoKeywords,
    path: "/channels",
    jsonLd,
  });

  return (
    <Layout>
      <PageHero
        eyebrow="四类投稿通道"
        title="选择通道，把线索安全交出来"
        subtitle="不同素材适合不同的提交方式。图文、私信、视频、录音四条通道相互独立，你可以只选最合适的那一个，也可以先沟通、再补充。"
        image="https://readdy.ai/api/search-image?query=Abstract%20dark%20green%20composition%20with%20interlocking%20geometric%20channel%20shapes%20and%20thin%20amber%20light%20lines%2C%20subtle%20grain%20texture%2C%20deep%20shadows%2C%20minimal%20editorial%20style%2C%20wide%20negative%20space%20for%20headline%20text%2C%20high%20contrast%2C%20no%20text%2C%20no%20people&width=1600&height=700&seq=qs-channels-a1&orientation=landscape"
        imageAlt="交错几何通道与细线构成抽象图形，象征潜声投稿的四类匿名投稿通道"
        crumbs={[{ label: "首页", to: "/" }, { label: "投稿通道" }]}
      />

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="通道说明"
              title="四条通道，各自解决一种情况"
              desc="如果不确定该选哪一条，可以先看下面的适用场景；实在拿不准，用图文通道说明情况也可以，编辑会告诉你下一步怎么补。"
            />
          </Reveal>
          <div className="mt-6">
            {channels.map((channel, index) => (
              <ChannelDetail
                key={channel.id}
                index={index}
                slug={channel.slug}
                icon={channel.icon}
                name={channel.name}
                summary={channel.summary}
                scenes={channel.scenes}
                requirements={channel.requirements}
                tag={channel.tag}
              />
            ))}
          </div>
        </div>
      </section>

      <TipForm />

      <section className="w-full px-4 md:px-8 py-16 md:py-20">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                eyebrow="投稿须知"
                title="提交之前，请留意这几点"
                desc="这些提醒不能替代法律意见，但可以帮你避开一些常见的坑。"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-4">
              {noticeList.map((notice, index) => (
                <Reveal key={notice} delay={index * 70}>
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-medium shrink-0">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-relaxed text-foreground-700">{notice}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={120}>
              <p className="mt-8 text-sm leading-relaxed text-foreground-600">
                第一次投稿，建议先读一遍
                <Link to="/guide" className="text-primary-600 hover:text-primary-700 underline underline-offset-4 mx-1">
                  安全投稿指南
                </Link>
                ，里面有一份可以直接照着准备的素材清单。
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand
        title="不确定选哪条通道？"
        desc="先看安全指南里「如何选通道」的部分，或者直接用图文通道说明情况，编辑会帮你判断后续怎么补材料。"
        primaryLabel="阅读安全投稿指南"
        primaryTo="/guide"
        secondaryLabel="查看常见问题"
        secondaryTo="/faq"
      />
    </Layout>
  );
}